import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import AppLayout from "../layouts/AppLayout.jsx";
import NavEmailMarketing from "../components/NavEmailMarketing.jsx";
import TemplateEmailCard from "../components/TemplateEmailCard.jsx";
import API from "../config/api.js";

export default function TemplatesEmailsLeadMagnet() {
  const location = useLocation();

  const [templates, setTemplates] = useState([]);
  const [activeCategories, setActiveCategories] = useState([]);
  const [isNew, setIsNew] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams(location.search);
      const search = params.get("search") || "";

      const formData = new FormData();
      formData.append("currentPage", page);

      if (isNew) {
        formData.append("new", "true");
      }

      if (search.trim()) {
        formData.append("search", search.trim());
      }

      activeCategories.forEach((id) => {
        formData.append("idcategory[]", id);
      });

      const res = await fetch(`${API.templates}?list&type=lead%20magnet`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await res.json();

      if (!Array.isArray(data) || data.length === 0) {
        if (page === 1) {
          setTemplates([]);
        }
        setHasMore(false);
        return;
      }

      if (page === 1) {
        setTemplates(data);
      } else {
        setTemplates((prev) => [...prev, ...data]);
      }
    } catch (err) {
      console.error("Error", err);

      if (page === 1) {
        setTemplates([]);
      }

      setHasMore(false);
    } finally {
      setLoading(false);
      setButtonDisabled(true);
      setTimeout(() => setButtonDisabled(false), 2000);
    }
  }, [location.search, page, isNew, activeCategories]);

  const openDraftPrompt = () => {
    const draftPrompt = document.getElementById("draft-prompt");
    if (draftPrompt && window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(draftPrompt).show();
    }
  };

  const applyTemplateToDraft = (template) => {
    if (!template) return;

    window._draft_prompt_clear?.();

    const ids = Array.isArray(template.idimages) ? template.idimages : [];

    ids.forEach((id) => {
      window._draft_prompt_item_add?.(
        "image",
        id,
        `${API.baseUrl}/upload/image/${id}/image/original.jpg`
      );
    });

    const contentInput = document.querySelector('#draft-prompt [name="content"]');
    if (contentInput) {
      contentInput.value = template.caption || "";
      contentInput.dispatchEvent(new Event("input", { bubbles: true }));
      contentInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    openDraftPrompt();
  };

  const handleUseTemplate = (template) => {
    applyTemplateToDraft(template);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ids = params.getAll("idcategory");

    const newFlag =
      params.has("new") &&
      (params.get("new") === "" ||
        params.get("new") === "true" ||
        params.get("new") === "1");

    setTemplates([]);
    setPage(1);
    setHasMore(true);
    setActiveCategories(ids);
    setIsNew(newFlag);
  }, [location.search]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Infinite scroll com IntersectionObserver
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!hasMore || loading || buttonDisabled) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, buttonDisabled]);

  const handleLoadMore = () => {
    if (!buttonDisabled && !loading && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const placeholders = Array.from({ length: 8 }, (_, i) => (
    <div key={`placeholder-${i}`} className="col-md-4 placeholder-item">
      <div className="card rounded-4 h-100 border animate__animated animate__fadeIn animate__slow">
        <div className="placeholder-glow">
          <div
            className="placeholder w-100 rounded-top-4"
            style={{ height: "200px" }}
          ></div>
        </div>
        <div className="card-body">
          <h5 className="card-title">
            <span className="placeholder col-7"></span>
          </h5>
          <p className="card-text">
            <span className="placeholder col-5"></span>
          </p>
        </div>
        <div className="card-footer rounded-bottom-4 bg-transparent p-3">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                type="button"
                className="btn btn-outline-primary disabled placeholder"
                disabled
              >
                Loading...
              </button>
            </div>
            <div className="col text-end">
              <span className="placeholder col-1 me-2"></span>
              <span className="placeholder col-1"></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  ));

  return (
    <AppLayout currentPage="emails-lead-magnet">
      <NavEmailMarketing currentPage="emails-lead-magnet" />

      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-4 g-2">
        {loading && templates.length === 0
          ? placeholders
          : templates.map((template) => (
              <div key={template.idtemplate} className="col-md-4">
                <TemplateEmailCard
                  template={template}
                  onUse={handleUseTemplate}
                />
              </div>
            ))}
      </div>

      {hasMore && templates.length > 0 && (
        <div className="text-center mt-4" ref={sentinelRef}>
          <button
            type="button"
            onClick={handleLoadMore}
            className="btn btn-primary"
            disabled={buttonDisabled || loading}
          >
            {loading ? "Loading..." : "Load More Templates"}
          </button>
        </div>
      )}
    </AppLayout>
  );
}